import { useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useClerk, useOrganizationList } from "@clerk/react";
import { Icone, type NomeIcone } from "@/components/Icone";
import { ChaveTema, Dica } from "@/components/casca/ChaveTema";
import type { AreaKey } from "@/lib/permissoes";
import type { ClinicaResumo } from "@/lib/clinica";
import { conversasQuery } from "@/web/src/consultas";

/**
 * Os destinos do menu: cinco, e não as dez áreas soltas de antes.
 *
 * Quem viu o app achou o menu desorganizado — dez ícones lado a lado, sem
 * ordem que se explicasse. As áreas continuam as mesmas (e as mesmas guardas,
 * em `lib/permissoes.ts`); o que muda é o agrupamento:
 *   - Resultados = Relatório + Funil (as duas respondem "está compensando?");
 *   - Ajustes    = Empresa, Modelos, Conexão, Equipe e Configurações.
 * Cada destino leva à PRIMEIRA área do grupo que a pessoa pode abrir — a
 * recepção, que não vê Empresa, cai direto em Modelos.
 */
const DESTINOS: Array<{ rotulo: string; icone: NomeIcone; areas: AreaKey[]; tambem?: string[]; noFim?: boolean }> = [
  { rotulo: "Conversas", icone: "conversas", areas: ["conversas"] },
  { rotulo: "Agenda", icone: "agenda", areas: ["agenda"] },
  { rotulo: "Pacientes", icone: "pacientes", areas: ["pacientes"] },
  { rotulo: "Resultados", icone: "relatorio", areas: ["relatorio", "funil"] },
  {
    rotulo: "Ajustes",
    icone: "ajustes",
    areas: ["empresa", "modelos", "conexao", "equipe", "configuracoes"],
    tambem: ["/sandbox"],
    noFim: true,
  },
];

const CAMINHO: Record<AreaKey, string> = {
  conversas: "/conversas",
  pacientes: "/pacientes",
  agenda: "/agenda",
  funil: "/funil",
  relatorio: "/relatorio",
  modelos: "/modelos",
  empresa: "/empresa",
  conexao: "/conexao",
  equipe: "/equipe",
  configuracoes: "/configuracoes",
};

/**
 * O menu: uma coluna estreita só de ícones, clara no tema claro e chumbo no
 * escuro. O nome de cada destino aparece numa etiqueta ao passar o mouse.
 *
 * A barra fica aberta o dia todo ao lado de uma conversa — cada pixel que ela
 * toma é pixel que a conversa perde. No celular ela vira a barra de baixo,
 * com o nome pequeno sob o ícone (no toque não existe "passar o mouse").
 *
 * Tudo que ela mostra chega como prop, JÁ resolvido: áreas e clínica
 * vêm de `/api/painel/eu`, que o roteador busca antes de desenhar a casca.
 */
export function CascaApp({
  clinica,
  usuario,
  areas,
  donoDaPlataforma,
}: {
  clinica: ClinicaResumo;
  usuario: { nome: string; email: string; avatar: string };
  areas: readonly AreaKey[];
  donoDaPlataforma: boolean;
}) {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const permitidas = new Set(areas);
  const em = (href: string) => pathname === href || pathname.startsWith(href + "/");

  // Quantas conversas esperam alguém da equipe: o número vermelho no ícone.
  // A lista já é ouvida pelo aviso em tempo real, então o número anda sozinho.
  const { data: lista } = useQuery({ ...conversasQuery(), enabled: permitidas.has("conversas") });
  const aguardando = lista?.aguardandoEquipe.length ?? 0;

  const destinos = DESTINOS.flatMap((d) => {
    const abre = d.areas.filter((a) => permitidas.has(a));
    if (abre.length === 0) return [];
    const caminhos = [...abre.map((a) => CAMINHO[a]), ...(d.tambem ?? [])];
    return [{ ...d, href: CAMINHO[abre[0]], ativo: caminhos.some(em), badge: d.areas[0] === "conversas" ? aguardando : 0 }];
  });
  if (donoDaPlataforma) {
    const ajustes = destinos.findIndex((d) => d.noFim);
    const sistema = { rotulo: "Sistema", icone: "sistema" as NomeIcone, areas: [], href: "/sistema", ativo: em("/sistema"), badge: 0, noFim: true };
    destinos.splice(ajustes === -1 ? destinos.length : ajustes, 0, sistema);
  }
  const primeiroDoFim = destinos.findIndex((d) => d.noFim);

  return (
    <nav
      aria-label="Menu principal"
      className="relative z-30 flex h-[64px] w-full shrink-0 items-stretch border-t border-[var(--color-borda)] bg-[var(--color-sidebar)] px-1 select-none md:h-auto md:w-[68px] md:flex-col md:items-center md:gap-4 md:border-t-0 md:border-r md:px-0 md:py-3.5"
    >
      <div className="hidden md:block">
        <SeletorClinica clinica={clinica} />
      </div>

      <div className="flex flex-1 items-stretch justify-around md:w-full md:flex-col md:items-center md:justify-start md:gap-1.5">
        {destinos.map((d, i) => (
          <Link
            key={d.href}
            to={d.href}
            aria-label={d.badge ? `${d.rotulo} — ${d.badge} aguardando a equipe` : d.rotulo}
            aria-current={d.ativo ? "page" : undefined}
            className={
              "group/dica relative flex flex-1 flex-col items-center justify-center gap-0.5 md:h-[42px] md:w-[46px] md:flex-none " +
              (i === primeiroDoFim ? "md:mt-auto " : "") +
              (d.ativo ? "text-[var(--color-marca-forte)]" : "text-[var(--color-suave)] hover:text-[var(--color-tinta)]")
            }
          >
            <span
              className={
                "relative flex h-[30px] w-[52px] items-center justify-center rounded-full transition-colors md:h-[42px] md:w-[46px] md:rounded-xl " +
                (d.ativo ? "bg-[var(--color-sidebar-active)]" : "group-hover/dica:bg-[var(--color-painel)]")
              }
            >
              <Icone nome={d.icone} className="h-5 w-5" />
              {d.badge > 0 && (
                <span className="absolute -top-1 right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-[var(--color-sidebar)] bg-[var(--color-alerta)] px-1 text-[10.5px] font-bold text-[var(--color-fundo)] md:right-0">
                  {d.badge}
                </span>
              )}
            </span>
            <span className="text-[11px] font-semibold md:hidden">{d.rotulo}</span>
            <span className="hidden md:contents">
              <Dica>{d.rotulo}</Dica>
            </span>
          </Link>
        ))}
      </div>

      <div className="hidden flex-col items-center gap-3 md:flex">
        <ChaveTema dica />
        <Perfil usuario={usuario} />
      </div>
    </nav>
  );
}

/** Fecha um menu ao clicar fora e no Esc: um menu que só fecha no próprio botão prende quem desistiu. */
function useFecharFora(aberto: boolean, fechar: () => void) {
  const caixa = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const clique = (e: MouseEvent) => {
      if (!caixa.current?.contains(e.target as Node)) fechar();
    };
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && fechar();
    document.addEventListener("mousedown", clique);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", clique);
      document.removeEventListener("keydown", tecla);
    };
  }, [aberto, fechar]);
  return caixa;
}

const MENU =
  "anim-scale absolute left-[56px] z-50 w-64 rounded-[14px] bg-[var(--color-elevado)] p-1.5 text-[var(--color-tinta)] shadow-flutua";
const ITEM_MENU = "flex w-full items-center gap-2 rounded-[10px] px-3 py-2 text-left text-[13px] transition-colors";

/**
 * A clínica ativa, no topo do menu.
 *
 * O nome vem do SERVIDOR (banco), então já está certo no primeiro desenho; o
 * hook do Clerk só entra para listar as outras e trocar. Quem tem uma clínica
 * só nunca vê o menu abrir.
 */
function SeletorClinica({ clinica }: { clinica: ClinicaResumo }) {
  const [aberto, setAberto] = useState(false);
  const [trocando, setTrocando] = useState<string | null>(null);
  const caixa = useFecharFora(aberto, () => setAberto(false));
  const { setActive, userMemberships } = useOrganizationList({ userMemberships: { infinite: true } });

  const lista = userMemberships?.data ?? [];
  const temOutras = lista.length > 1;

  async function trocar(orgId: string) {
    if (!setActive || orgId === clinica.id) return setAberto(false);
    setTrocando(orgId);
    // Recarrega de verdade, e não só o que está guardado: a clínica decide de
    // QUEM é todo dado da tela, e uma troca parcial deixaria listas da
    // clínica anterior penduradas até cada uma ser buscada de novo.
    await setActive({ organization: orgId });
    window.location.href = "/conversas";
  }

  return (
    <div className="relative" ref={caixa}>
      <button
        type="button"
        onClick={() => temOutras && setAberto((v) => !v)}
        aria-label={temOutras ? `${clinica.nome} — trocar de clínica` : clinica.nome}
        aria-expanded={temOutras ? aberto : undefined}
        className="group/dica relative flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-marca)] text-base font-bold text-[var(--color-sobre-marca)] transition-transform active:scale-95"
      >
        {clinica.nome.slice(0, 1).toUpperCase()}
        {!aberto && <Dica>{clinica.nome}</Dica>}
      </button>

      {aberto && (
        <div className={`${MENU} top-0`}>
          <div className="border-b border-[var(--color-borda)] px-3 pt-1.5 pb-2.5">
            <p className="rotulo text-[10.5px]">Clínica atual</p>
            <p className="truncate text-sm font-semibold">{clinica.nome}</p>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            <p className="rotulo px-3 pt-1.5 pb-1 text-[10.5px]">Alternar para</p>
            {lista.map((m) => {
              const org = m.organization;
              const atual = org.id === clinica.id;
              return (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => void trocar(org.id)}
                  disabled={trocando !== null}
                  className={
                    `${ITEM_MENU} justify-between ` +
                    (atual ? "bg-[var(--color-realce)] font-semibold text-[var(--color-marca-forte)]" : "hover:bg-[var(--color-painel)]")
                  }
                >
                  <span className="truncate">{org.name}</span>
                  {atual && <Icone nome="check" className="h-3.5 w-3.5" />}
                </button>
              );
            })}
          </div>
          <div className="border-t border-[var(--color-borda)] pt-1">
            <Link
              to="/onboarding"
              search={{ nova: true }}
              onClick={() => setAberto(false)}
              className={`${ITEM_MENU} font-medium text-[var(--color-suave)] hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]`}
            >
              <Icone nome="mais" className="h-3.5 w-3.5" />
              Criar outra clínica
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** Quem está usando, e a saída. Nosso, não o `<UserButton>` do Clerk. */
function Perfil({ usuario }: { usuario: { nome: string; email: string; avatar: string } }) {
  const [aberto, setAberto] = useState(false);
  const caixa = useFecharFora(aberto, () => setAberto(false));
  const { signOut } = useClerk();

  return (
    <div className="relative" ref={caixa}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label={`${usuario.nome} — sua conta`}
        aria-expanded={aberto}
        className="group/dica relative flex h-9 w-9 items-center justify-center rounded-full"
      >
        {usuario.avatar ? (
          <img src={usuario.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-info-fundo)] text-[13px] font-bold text-[var(--color-info)]">
            {usuario.nome.slice(0, 1).toUpperCase()}
          </span>
        )}
        {!aberto && <Dica>Sua conta</Dica>}
      </button>

      {aberto && (
        <div className={`${MENU} bottom-0 w-60`}>
          <div className="border-b border-[var(--color-borda)] px-3 pt-1.5 pb-2.5">
            <p className="truncate text-sm font-semibold">{usuario.nome}</p>
            <p className="truncate text-xs text-[var(--color-suave)]">{usuario.email}</p>
          </div>
          <div className="pt-1">
            <Link
              to="/configuracoes"
              onClick={() => setAberto(false)}
              className={`${ITEM_MENU} hover:bg-[var(--color-painel)]`}
            >
              <Icone nome="pessoa" className="h-3.5 w-3.5" />
              Minha conta
            </Link>
            <button
              type="button"
              onClick={() => void signOut({ redirectUrl: "/sign-in" })}
              className={`${ITEM_MENU} font-medium text-[var(--color-alerta)] hover:bg-[var(--color-alerta-fundo)]`}
            >
              <Icone nome="sair" className="h-3.5 w-3.5" />
              Sair da conta
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
